import { Box } from '@mantine/core'
import { useForm, zodResolver } from '@mantine/form'
import dayjs from 'dayjs'
import { z } from 'zod'

import { Button } from '@/components/Button/Button'
import { NumberInput } from '@/components/NumberInput/NumberInput'
import { SeparateDateInput } from '@/components/SeparateDateInput/SeparateDateInput'
import { TextInput } from '@/components/TextInput/TextInput'
import { Title } from '@/components/Title/Title'

import type { GoalStepData } from '@/__generated__/requests'

import { useAddGoalStep } from '@/hooks/useGoalMutations.hook'

type GoalStepsFormProps = {
  goalId: number
  onStepAdded: (step: GoalStepData) => void
}

const stepSchema = z.object({
  targetAmount: z.number().optional(),
  targetDate: z.string().optional(),
  title: z.string().min(1, 'Title is required'),
})

type StepFormValues = z.infer<typeof stepSchema>

export function GoalStepsForm({ goalId, onStepAdded }: GoalStepsFormProps) {
  const form = useForm<StepFormValues>({
    initialValues: {
      targetAmount: undefined,
      targetDate: '',
      title: '',
    },
    validate: zodResolver(stepSchema),
  })

  const addStepMutation = useAddGoalStep(() => {
    form.reset()
  })

  const handleAddStep = (values: StepFormValues) => {
    addStepMutation.mutate(
      {
        goalId,
        requestBody: {
          targetAmount: values.targetAmount || undefined,
          targetDate: values.targetDate || undefined,
          title: values.title,
        },
      },
      {
        onSuccess: (data: GoalStepData) => {
          onStepAdded(data)
        },
      },
    )
  }

  return (
    <Box
      p={24}
      style={{
        backgroundColor: 'var(--mantine-other-colors-white)',
        borderRadius: 8,
        boxShadow: '0px 4px 12px 0px rgba(0, 0, 0, 0.04)',
      }}
    >
      <Title mb={16} variant="h2_small">
        Add step
      </Title>

      <Box
        component="form"
        maw={400}
        noValidate
        onSubmit={form.onSubmit(handleAddStep)}
      >
        <TextInput
          label="Step title"
          placeholder="Enter step title"
          {...form.getInputProps('title')}
        />
        <SeparateDateInput
          label="Target date"
          onChange={value => {
            if (value instanceof Date) {
              form.setFieldValue(
                'targetDate',
                dayjs(value).format('YYYY-MM-DD'),
              )
            } else {
              form.setFieldValue('targetDate', '')
            }
          }}
          value={
            form.values.targetDate
              ? dayjs(form.values.targetDate).toDate()
              : undefined
          }
        />
        <NumberInput
          hideControls
          label="Target amount"
          placeholder="Enter amount"
          {...form.getInputProps('targetAmount')}
        />
        <Button
          loading={addStepMutation.isPending}
          mt={16}
          type="submit"
          variant="primary"
        >
          Add step
        </Button>
      </Box>
    </Box>
  )
}
